/**
 * Nothing from one account may show under another (Shardul, 08 Oct 26: signed
 * out of fly@zoomlocal.in, signed in as shardul.pujari@zoomlocal.in, pressed
 * Back, and the first account's Ekdant Dental Clinic opened with its data:
 * "this is a security leak issue and it shouldnt happen at all").
 *
 * The server now opens a location only to an account it belongs to
 * (_shared/session-link.ts { allowStaff: false }). This is the browser's half.
 * The agent pages keep tab copies of what they last showed (agent-home:<...>,
 * the switcher, the page trail, scroll places), keyed by location, not by
 * account, and painted before the server answers. So:
 *
 *   - signing out drops every such copy at once;
 *   - a different account signing in on this tab drops them too and reloads
 *     the page, which also empties every in-memory cache (the overview, the
 *     switcher, the Live agent); the same account signing back in keeps them;
 *   - a page opened fresh (a sign-in round trip through Google) for a
 *     different account than the tab last had drops them before anything
 *     paints, with no extra reload.
 *
 * Preferences that hold no account data stay (the theme, the home view), and
 * so does a Google connect or sign-in that is under way (agent_oauth_state,
 * fly-start-return).
 */

/** The account this tab last had signed in; kept across a sign-out so the next sign-in can be compared. */
export const TAB_USER_KEY = 'fly-tab-user';

/** The tab storage that holds one account's data. */
const ACCOUNT_DATA_PREFIXES = ['agent-', 'agent_reconnect_from:', 'fly-app-ws', 'qa-suite:'];

/** The keys among these that hold account data. Pure. */
export function accountDataKeys(keys: string[]): string[] {
  return keys.filter((k) => ACCOUNT_DATA_PREFIXES.some((p) => k.startsWith(p)));
}

/** Drops this tab's copies of the last account's data. Returns how many went. */
export function wipeAccountData(storage: Storage | null = safeSession()): number {
  if (!storage) return 0;
  const keys: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const k = storage.key(i);
    if (k) keys.push(k);
  }
  const doomed = accountDataKeys(keys);
  for (const k of doomed) storage.removeItem(k);
  return doomed.length;
}

/** What to do when the tab's signed-in account is seen. Pure. */
export function onAccountSeen(
  previous: string | null,
  current: string | null,
  event: 'initial' | 'signed_in' | 'signed_out' | 'other',
): 'nothing' | 'wipe' | 'wipe_and_reload' {
  if (event === 'signed_out') return 'wipe';
  if (!current || !previous || previous === current) return 'nothing';
  // A different account than this tab last had: a fresh page wipes before anything paints; a live page reloads too.
  return event === 'initial' ? 'wipe' : 'wipe_and_reload';
}

function safeSession(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

function readTabUser(storage: Storage | null): string | null {
  try {
    return storage?.getItem(TAB_USER_KEY) ?? null;
  } catch {
    return null;
  }
}

function writeTabUser(storage: Storage | null, id: string): void {
  try {
    storage?.setItem(TAB_USER_KEY, id);
  } catch {
    /* private mode: nothing kept, nothing to compare next time */
  }
}

interface AuthLike {
  onAuthStateChange(cb: (event: string, session: { user?: { id?: string } | null } | null) => void): unknown;
}

let installed = false;

/** Watches the signed-in account for this tab, once per page load. */
export function installAccountSwitchGuard(auth: AuthLike, reload: () => void = () => window.location.reload()): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  auth.onAuthStateChange((event, session) => {
    const storage = safeSession();
    const current = session?.user?.id ?? null;
    const previous = readTabUser(storage);
    const kind = event === 'INITIAL_SESSION' ? 'initial' : event === 'SIGNED_IN' ? 'signed_in' : event === 'SIGNED_OUT' ? 'signed_out' : 'other';
    const act = onAccountSeen(previous, current, kind);
    if (act !== 'nothing') {
      const dropped = wipeAccountData(storage);
      console.log(`[accountSwitch] ${event}: ${act === 'wipe' ? 'dropped' : 'dropped and reloading,'} ${dropped} tab cop${dropped === 1 ? 'y' : 'ies'} of the last account's data`);
    }
    if (current) writeTabUser(storage, current);
    if (act === 'wipe_and_reload') reload();
  });
}

/** For tests: lets a fresh guard be installed. */
export function resetAccountSwitchGuard(): void {
  installed = false;
}
